/**
 * istanbul ignore file
 *
 * @format
 */

import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, take } from 'rxjs/operators';

import { AUTH_URL_FILTER } from './injection-tokens/auth0-url-filter.token';
import { AuthenticationService } from './authentication.service';
import { HttpService, isVHHeaderAnAOSHeader } from '@shared/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
	/**
	 * @description
	 * Flag to determine if being viewed on an AOS device
	 */
	private isAOS = isVHHeaderAnAOSHeader(
		this.httpService.getRequestHeaderValueAndSetStateKey('DSG_VH')
	);

	constructor(
		@Inject(AUTH_URL_FILTER) protected authUrlFilter: string[] = [],
		private authService: AuthenticationService,
		private httpService: HttpService
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// if request is AOS or not a filtered request, continue with the Request as-is
		if (this.isAOS || typeof this.filterReq(req) !== 'string') {
			return next.handle(req);
		}

		// if request contains one of the authEndpoints continue with authorization checks
		return this.authService.getIsAuthenticated$().pipe(
			catchError(() => {
				return of(false);
			}),
			take(1),
			mergeMap((loggedIn: boolean) => {
				// if not logged in then move on
				if (!loggedIn) {
					return next.handle(req);
				}

				return this.authService.getTokenSilently$().pipe(
					catchError(() => {
						return of(null);
					}),
					take(1),
					mergeMap((token: string) => {
						if (token) {
							const tokenReq = req.clone({
								setHeaders: { Authorization: token }
							});

							return next.handle(tokenReq);
						}

						return next.handle(req);
					}),
					catchError((err) => {
						return throwError(err);
					})
				);
			}),
			catchError((err) => {
				return throwError(err);
			})
		);
	}

	private filterReq(req: HttpRequest<any>): string {
		return this.authUrlFilter.find((endpoint: string): boolean => {
			return req.url.includes(endpoint);
		});
	}
}
